import { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import type { LocationDescriptor } from 'history';
import type { ReactNode, CSSProperties } from 'react';
import type { DefaultTheme } from 'styled-components';

/* Styled Components */
import { Button as StyledButton } from 'styles/common/Button';

const StyledLinkButton = StyledButton.withComponent(Link);

export const BUTTON_COLOR_KEYS = {
  DARK_COBALT: 1,
  BLUE1: 2,
  ORANGE: 3,
} as const;

export const BUTTON_SIZE_KEYS = {
  SMALL: 1,
  MEDIUM: 2,
  LARGE: 3,
  EXTRA_LARGE: 4,
} as const;

type PropsType = {
  size?: Values<typeof BUTTON_SIZE_KEYS>;
  color?: Values<typeof BUTTON_COLOR_KEYS>;
  children: ReactNode;
  disabled?: boolean;
  testId?: string;
  stretch?: boolean;
  onClick?: () => void;
  to?: LocationDescriptor;
  target?: string;
  bold?: boolean;
  overrideStyle?: CSSProperties;
  onClickDisabled?: () => void;
  padding?: string;
  download?: boolean;
};

export const BUTTON_COLOR = (theme: DefaultTheme) => ({
  [BUTTON_COLOR_KEYS.DARK_COBALT]: {
    color: theme.white,
    backgroundColor: theme.darkCobalt,
  },
  [BUTTON_COLOR_KEYS.BLUE1]: {
    color: theme.white,
    backgroundColor: theme.Blue1,
  },
  [BUTTON_COLOR_KEYS.ORANGE]: {
    color: theme.white,
    backgroundColor: theme.wishOrange,
  },
});

export const BUTTON_SIZE = {
  [BUTTON_SIZE_KEYS.SMALL]: {
    fontSize: 14,
    fontWeight: 400,
    height: 32,
  },
  [BUTTON_SIZE_KEYS.MEDIUM]: {
    fontSize: 16,
    fontWeight: 700,
    height: 40,
  },
  [BUTTON_SIZE_KEYS.LARGE]: {
    fontSize: 16,
    fontWeight: 700,
    height: 48,
  },
  [BUTTON_SIZE_KEYS.EXTRA_LARGE]: {
    fontSize: 24,
    fontWeight: 400,
    height: 48,
  },
} as const;

const Button = ({
  size = BUTTON_SIZE_KEYS.MEDIUM,
  color = BUTTON_COLOR_KEYS.DARK_COBALT,
  disabled,
  testId,
  stretch,
  onClick,
  children,
  to,
  target,
  bold,
  overrideStyle = {},
  onClickDisabled,
  padding,
  download,
}: PropsType) => {
  const theme = useTheme();
  const formattedStyle = {
    ...BUTTON_COLOR(theme)[color],
    ...BUTTON_SIZE[size],
  };

  return to ? (
    <StyledLinkButton
      size={size}
      color={color}
      stretch={stretch ? 1 : 0}
      onClick={!disabled ? onClick : onClickDisabled}
      disabled={disabled}
      to={to}
      href={to}
      target={target}
      rel="noopener noreferrer"
      bold={bold ? 1 : 0}
      padding={padding}
      download={download}
      data-testid={testId}
      style={{
        ...formattedStyle,
        ...overrideStyle,
      }}
    >
      {children}
    </StyledLinkButton>
  ) : (
    <StyledButton
      size={size}
      color={color}
      stretch={stretch}
      onClick={!disabled ? onClick : onClickDisabled}
      disabled={disabled}
      to={to}
      href={to}
      target={target}
      role="button"
      rel="noopener noreferrer"
      bold={bold}
      padding={padding}
      data-testid={testId}
      style={{
        ...formattedStyle,
        ...overrideStyle,
      }}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
